export interface Venue {
  id: number;
  tm_id: string | null;
  name: string;
  slug: string;
  address: string | null;
  city: string | null;
  /** Catalogue city for routing; the venue may be in a surrounding municipality. */
  city_slug?: string;
  latitude: number;
  longitude: number;
  url: string | null;
  image_url: string | null;
}

export interface Attraction {
  id: number;
  tm_id: string | null;
  name: string;
  slug: string;
  image_url: string | null;
  genres: string[];
  spotify_id: string | null;
  external_links?: Record<string, { url: string }[]> | null;
}

export interface Event {
  id: number;
  tm_id: string | null;
  name: string;
  slug: string;
  url: string | null;
  image_url: string | null;
  images: EventImage[];
  local_date: string | null;
  local_time: string | null;
  start_datetime: string | null;
  status: string | null;
  price_min: number | null;
  price_max: number | null;
  price_currency: string | null;
  info: string | null;
  please_note: string | null;
  image_focus: string | null;
  venue: Venue;
  attractions: Attraction[];
}

export interface EventImage {
  url: string;
  width: number;
  height: number;
  ratio?: string;
  /** Feed-supplied generic placeholder, not art for this event */
  fallback?: boolean;
}

export interface VenueWithEvents {
  venue: Venue;
  events: Event[];
}

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface CityConfig {
  slug: string;
  name: string;
  country: string;
  center: { lat: number; lng: number };
  zoom: number;
  dbName: string; // city name as stored in DB (for RPC query)
  timezone: string; // IANA timezone (e.g. "America/Chicago")
}

// Raw row returned by get_city_events_v2 RPC (attractions pre-joined as JSONB)
export interface CityEventRowV2 {
  event_id: number;
  event_tm_id: string | null;
  event_name: string;
  event_url: string | null;
  event_image_url: string | null;
  local_date: string | null;
  local_time: string | null;
  start_datetime: string | null;
  status: string | null;
  price_min: number | null;
  price_max: number | null;
  price_currency: string | null;
  info: string | null;
  image_focus: string | null;
  event_slug: string | null;
  venue_id: number;
  venue_tm_id: string | null;
  venue_name: string;
  venue_address: string | null;
  venue_city: string | null;
  venue_latitude: number;
  venue_longitude: number;
  venue_url: string | null;
  venue_image_url: string | null;
  venue_slug: string | null;
  attractions: {
    id: number;
    tm_id: string | null;
    name: string;
    slug: string;
    image_url: string | null;
    genres: string[];
    spotify_id: string | null;
  }[];
}

// Raw row returned by get_city_events RPC
export interface CityEventRow {
  event_id: number;
  event_tm_id: string | null;
  event_name: string;
  event_url: string | null;
  event_image_url: string | null;
  event_images: EventImage[] | null;
  local_date: string | null;
  local_time: string | null;
  start_datetime: string | null;
  status: string | null;
  price_min: number | null;
  price_max: number | null;
  price_currency: string | null;
  info: string | null;
  please_note: string | null;
  image_focus: string | null;
  event_slug: string | null;
  venue_id: number;
  venue_tm_id: string | null;
  venue_name: string;
  venue_address: string | null;
  venue_city: string | null;
  venue_latitude: number;
  venue_longitude: number;
  venue_url: string | null;
  venue_image_url: string | null;
  venue_slug: string | null;
  // Only present in get_event_by_slug response
  festival_id?: number | null;
  festival_slug?: string | null;
  festival_name?: string | null;
  hidden?: boolean;
  parent_event_id?: number | null;
}

// Raw row from event_attractions join
export interface EventAttractionRow {
  event_id: number;
  attraction_id: number;
  attraction_tm_id: string | null;
  attraction_name: string;
  attraction_image_url: string | null;
  attraction_genres: string[];
  attraction_spotify_id: string | null;
  attraction_slug: string;
  attraction_external_links?: Record<string, { url: string }[]> | null;
}

// Related showing from get_related_showings RPC
export interface RelatedShowing {
  event_id: number;
  event_name: string;
  event_url: string | null;
  event_slug: string;
  local_date: string | null;
  local_time: string | null;
  price_min: number | null;
  price_max: number | null;
  price_currency: string | null;
}

// Auth & subscription types
export interface Profile {
  id: string;
  display_name: string | null;
  email: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface Subscription {
  id: number;
  user_id: string;
  subscription_type: "artist" | "venue" | "show";
  attraction_id: number | null;
  venue_id: number | null;
  event_id: number | null;
  created_at: string;
  attraction?: Attraction;
  venue?: Venue;
  event?: {
    name: string;
    slug: string;
    image_url: string | null;
    local_date: string | null;
    local_time: string | null;
    venue_name: string;
  };
}

export interface PendingSubscription {
  type: "artist" | "venue" | "show";
  targetId: number;
  targetName: string;
  targetImage?: string | null;
}

// Festival types

export interface Festival {
  id: number;
  name: string;
  slug: string;
  city_slug: string | null;
  description: string | null;
  url: string | null;
  image_url: string | null;
  start_date: string | null;
  end_date: string | null;
  venue: Venue | null;
}

export interface FestivalDay {
  date: string;
  event_id: number;
  attractions: FestivalLineupEntry[];
}

export interface FestivalSummary {
  id: number;
  name: string;
  slug: string;
  url: string | null;
  image_url: string | null;
  start_date: string;
  end_date: string;
  venue_name: string | null;
  venue_slug: string | null;
  day_dates: string[];
  total_artists: number;
  city_slug?: string;
}

export interface FestivalAttraction extends Attraction {
  is_activity?: false;
  stage: string | null;
  set_time: string | null;
  set_end_time: string | null;
  sort_order: number | null;
  child_event?: {
    slug: string;
    city_slug: string;
    artist_name: string;
  };
}

export interface FestivalActivity {
  is_activity: true;
  activity_name: string;
  stage: string | null;
  set_time: string | null;
  set_end_time: string | null;
  sort_order: number | null;
}

export type FestivalLineupEntry = FestivalAttraction | FestivalActivity;

export function isActivity(entry: FestivalLineupEntry): entry is FestivalActivity {
  return entry.is_activity === true;
}

export interface UserFestivalScheduleSummary {
  festivalId: number;
  festivalName: string;
  festivalSlug: string;
  festivalCitySlug: string | null;
  festivalImageUrl: string | null;
  festivalStartDate: string | null;
  festivalEndDate: string | null;
  savedSetCount: number;
}
